<template>
  <div class="d-flex align-center justify-center">
    <input
      :id="`order-input-${invitee.signeeId}`"
      v-no-password-manager
      autocomplete="off"
      class="round-input pointer-events-auto"
      data-test-id="update-order-input"
      max="9"
      min="1"
      type="number"
      :value="groupIndex"
      @change="handleUpdateGroupOrderDebounced"
      @focusin="emit('disable-drag', true)"
      @focusout="emit('disable-drag', false)"
    />
  </div>
</template>

<script setup lang="ts">
import type { Signee } from '@/types/Signee.ts';
import { type PropType } from 'vue';
import { useCreateStore } from '@/stores/create.ts';
import type { HTMLInputEvent } from '@/utils/types/HtmlInputEvent.ts';
import { debounce } from '@/composables/useUtils.ts';
import { vNoPasswordManager } from '@deepcloud/deep-ui-lib';

const props = defineProps({
  invitee: {
    type: Object as PropType<Signee>,
    required: true
  },
  groupIndex: {
    type: Number,
    required: true
  }
});

const emit = defineEmits(['disable-drag']);

const createStore = useCreateStore();

const handleUpdateGroupOrderDebounced = debounce(function (event: Event) {
  updateGroupOrder(event);
}, 700);

// Local reorder only: like every participant edit in the signing-workflow
// dialog, the new sign order is persisted in one batch by
// reconcileParticipants() on Confirm.
function updateGroupOrder(event: Event) {
  const fromGroupIndex = props.groupIndex - 1;
  const toGroupIndex = parseInt((event as HTMLInputEvent).target.value) - 1;

  if (isNaN(toGroupIndex) || fromGroupIndex === toGroupIndex) {
    return;
  }

  const insertionArrayGroupIndex = getGroupIndex(toGroupIndex);
  const inviteeWithUpdatedSignGroup: Signee = {
    ...props.invitee,
    signOrder: insertionArrayGroupIndex
  };
  const localSigneesOrdered = filterInviteeFromLocalData(props.invitee);
  const filteredGroups = localSigneesOrdered.filter(
    (group) => group.length > 0
  );
  if (insertionArrayGroupIndex < filteredGroups.length) {
    filteredGroups[insertionArrayGroupIndex].push(inviteeWithUpdatedSignGroup);
  } else if (insertionArrayGroupIndex >= filteredGroups.length) {
    filteredGroups.push([inviteeWithUpdatedSignGroup]);
  }

  // Renumber every member's `signOrder` to its group's new position (and
  // normalize the in-group order), exactly as the drag & drop handlers in
  // OrderedInvitees.vue do. Moving a signee out of a group can leave that group
  // empty and shift every later group down a position, so assigning the moved
  // invitee alone would leave the others' `signOrder` pointing at their OLD
  // position. The grouping sent on Confirm is derived from array positions and
  // would still be correct, but `signOrder` is what keys the pending quorum
  // edits (`setGroupQuorum` / `persistExplicitGroupQuorum` match a group by it)
  // — a stale value there silently matches no server group, dropping that
  // group's quorum on Confirm.
  createStore.updateSignOrderInLocalData(filteredGroups);
}

function getGroupIndex(toGroupIndex: number) {
  if (toGroupIndex <= 0) {
    return 0;
  } else if (toGroupIndex >= createStore.nextGroupIndex) {
    return createStore.nextGroupIndex;
  } else {
    return toGroupIndex;
  }
}

function filterInviteeFromLocalData(inviteeToRemove: Signee) {
  return createStore.signeesOrdered.map((group) =>
    group.filter((invitee) => invitee.signeeId !== inviteeToRemove.signeeId)
  );
}
</script>
<style lang="scss" scoped>
.round-input {
  z-index: 99;
  position: absolute;
  right: 40px;
  font-size: 16px;
  color: rgb(var(--v-theme-primary));
  background-color: rgb(var(--v-theme-primary), 0.2);
  border-color: transparent;
  text-align: center;
  height: 32px !important;
  width: 32px !important;
  min-height: 26px !important;
  min-width: 26px !important;
  border-radius: 50%;
}

/* Hide the spin buttons in WebKit browsers */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Hide spin buttons in Firefox */
input[type='number'] {
  -moz-appearance: textfield;
}
</style>
